import React, { createContext, useContext, useRef, useState } from "react";
import {
  AutoInvoice,
  AutoInvoiceItem,
  newAutoInvoice,
  newAutoInvoiceItem,
} from "../../../models/auto-invoice";
import Establishment from "../../../models/establishment";
import DispatchPoint from "../../../models/dispatch-point";
import Stamp from "../../../models/stamp";
import Department from "../../../models/department";
import District from "../../../models/district";
import City from "../../../models/city";
import Errors from "../../../models/errors";
import { PYG } from "../../../currency/available-currencies";
import moment from "moment";
import { changeTZToUtc } from "../../../utils/dates";

interface AutoInvoiceFormContextValue {
  // Refs and State
  autoInvoiceRef: React.MutableRefObject<AutoInvoice | null>;
  autoInvoiceDate: Date | undefined;
  currency: string;
  pygExchangeRate: number | undefined;
  totalAmount: number | undefined;
  applyDiscount: boolean;
  globalDiscount: number | undefined;
  establishment: Establishment | null;
  dispatchPoint: DispatchPoint | null;
  stamp: Stamp | null;
  providerDepartment: Department | null;
  providerDistrict: District | null;
  providerCity: City | null;
  transactionDepartment: Department | null;
  transactionDistrict: District | null;
  transactionCity: City | null;
  items: AutoInvoiceItem[];
  errors: Errors;
  readonly?: boolean;

  // Setters
  setAutoInvoiceDate: (date: Date | undefined) => void;
  setCurrency: (currency: string) => void;
  setPygExchangeRate: (rate: number | undefined) => void;
  setApplyDiscount: (apply: boolean) => void;
  setEstablishment: (est: Establishment | null) => void;
  setDispatchPoint: (dp: DispatchPoint | null) => void;
  setStamp: (stamp: Stamp | null) => void;
  setProviderDepartment: (dept: Department | null) => void;
  setProviderDistrict: (dist: District | null) => void;
  setProviderCity: (city: City | null) => void;
  setTransactionDepartment: (dept: Department | null) => void;
  setTransactionDistrict: (dist: District | null) => void;
  setTransactionCity: (city: City | null) => void;
  setItems: (items: AutoInvoiceItem[]) => void;
  setErrors: (errors: Errors) => void;

  // Handlers
  onEstablishmentChange: (newEstablishment: Establishment | null) => void;
  onDispatchPointChange: (newDispatchPoint: DispatchPoint | null) => void;
  onStampChange: (newStamp: Stamp | null) => void;
  onAutoInvoiceDateChange: (value: string | moment.Moment) => void;
  onCurrencyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPygExchangeRateChange: (newExchangeRate?: number) => void;
  onApplyDiscountChange: (applyDiscount: boolean) => void;
  onGlobalDiscountChange: (newGlobalDiscount?: number) => void;
  onProviderTypeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProviderDocumentTypeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onProviderDocumentNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onProviderFullNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProviderAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProviderHouseNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProviderDepartmentChange: (dept: Department | null) => void;
  onProviderDistrictChange: (dist: District | null) => void;
  onProviderCityChange: (city: City | null) => void;
  onTransactionAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTransactionHouseNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onTransactionDepartmentChange: (dept: Department | null) => void;
  onTransactionDistrictChange: (dist: District | null) => void;
  onTransactionCityChange: (city: City | null) => void;
  onCertificateNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCertificateCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTermChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onObsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onItemChange: (index: number, item: AutoInvoiceItem) => void;
  onItemDelete: (index: number) => void;
  onItemsChange: (newItems: AutoInvoiceItem[]) => void;
  onAddItemClick: () => void;
}

const AutoInvoiceFormContext = createContext<
  AutoInvoiceFormContextValue | undefined
>(undefined);

export const useAutoInvoiceFormContext = () => {
  const context = useContext(AutoInvoiceFormContext);
  if (!context) {
    throw new Error(
      "useAutoInvoiceFormContext must be used within AutoInvoiceFormProvider"
    );
  }
  return context;
};

interface AutoInvoiceFormProviderProps {
  children: React.ReactNode;
  taxPayerId?: number;
  readonly?: boolean;
}

export const AutoInvoiceFormProvider: React.FC<
  AutoInvoiceFormProviderProps
> = ({ children, taxPayerId, readonly }) => {
  const autoInvoiceRef = useRef<AutoInvoice | null>(newAutoInvoice(taxPayerId));
  const [autoInvoiceDate, setAutoInvoiceDate] = useState<Date | undefined>(
    undefined
  );
  const [currency, setCurrency] = useState(PYG);
  const [pygExchangeRate, setPygExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [globalDiscount, setGlobalDiscount] = useState<number | undefined>(
    undefined
  );
  const [establishment, setEstablishment] = useState<Establishment | null>(
    null
  );
  const [dispatchPoint, setDispatchPoint] = useState<DispatchPoint | null>(
    null
  );
  const [stamp, setStamp] = useState<Stamp | null>(null);
  const [providerDepartment, setProviderDepartment] =
    useState<Department | null>(null);
  const [providerDistrict, setProviderDistrict] = useState<District | null>(
    null
  );
  const [providerCity, setProviderCity] = useState<City | null>(null);
  const [transactionDepartment, setTransactionDepartment] =
    useState<Department | null>(null);
  const [transactionDistrict, setTransactionDistrict] =
    useState<District | null>(null);
  const [transactionCity, setTransactionCity] = useState<City | null>(null);
  const [items, setItems] = useState<AutoInvoiceItem[]>([]);
  const [errors, setErrors] = useState<Errors>({});

  const onEstablishmentChange = (newEstablishment: Establishment | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.establishmentId = newEstablishment?.id;
    autoInvoiceRef.current.establishmentCode = newEstablishment?.code;
    setEstablishment(newEstablishment);
  };

  const onDispatchPointChange = (newDispatchPoint: DispatchPoint | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.dispatchPointId = newDispatchPoint?.id;
    autoInvoiceRef.current.dispatchPointIdentifier =
      newDispatchPoint?.identifier;
    setDispatchPoint(newDispatchPoint);
  };

  const onStampChange = (newStamp: Stamp | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.stampId = newStamp?.id;
    autoInvoiceRef.current.stampIdentifier = newStamp?.identifier;
    autoInvoiceRef.current.stampBeginDate = newStamp?.beginDate;
    setStamp(newStamp);
  };

  const onAutoInvoiceDateChange = (value: string | moment.Moment) => {
    if (autoInvoiceRef.current === null) return;
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    const autoInvoiceDate = new Date((value as moment.Moment).toISOString());
    setAutoInvoiceDate(autoInvoiceDate);
    autoInvoiceRef.current.autoInvoiceDate =
      changeTZToUtc(autoInvoiceDate).toISOString();
  };

  const onCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoInvoiceRef.current === null) return;
    const newCurrency = e.target.value;
    autoInvoiceRef.current.currency = newCurrency;
    setCurrency(newCurrency);

    if (newCurrency === PYG) {
      autoInvoiceRef.current.pygExchangeRate = undefined;
      setPygExchangeRate(undefined);
    }
  };

  const onPygExchangeRateChange = (newExchangeRate?: number) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.pygExchangeRate = newExchangeRate;
    setPygExchangeRate(newExchangeRate);
  };

  const onApplyDiscountChange = (applyDiscount: boolean) => {
    if (autoInvoiceRef.current === null) return;
    setApplyDiscount(applyDiscount);
    if (!applyDiscount) {
      autoInvoiceRef.current.globalDiscount = undefined;
      setGlobalDiscount(undefined);
    }
  };

  const onGlobalDiscountChange = (newGlobalDiscount?: number) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.globalDiscount = newGlobalDiscount;
    setGlobalDiscount(newGlobalDiscount);
  };

  const onProviderTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.providerType = e.target.value;
  };

  const onProviderDocumentTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (autoInvoiceRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      autoInvoiceRef.current.providerDocumentType = e.target.value;
    } else {
      autoInvoiceRef.current.providerDocumentType = undefined;
    }
  };

  const onProviderDocumentNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.providerDocumentNumber = e.target.value;
  };

  const onProviderFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.providerFullName = e.target.value;
  };

  const onProviderAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.providerAddress = e.target.value;
  };

  const onProviderHouseNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (autoInvoiceRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      autoInvoiceRef.current.providerHouseNumber = parseInt(e.target.value);
    } else {
      autoInvoiceRef.current.providerHouseNumber = undefined;
    }
  };

  const onProviderDepartmentChange = (newDepartment: Department | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.providerDepartment = newDepartment?.code;
    autoInvoiceRef.current.providerDepartmentDescription =
      newDepartment?.description;
    setProviderDepartment(newDepartment);
    if (newDepartment === null) {
      setProviderDistrict(null);
      setProviderCity(null);
      autoInvoiceRef.current.providerDistrict = undefined;
      autoInvoiceRef.current.providerDistrictDescription = undefined;
      autoInvoiceRef.current.providerCity = undefined;
      autoInvoiceRef.current.providerCityDescription = undefined;
    }
  };

  const onProviderDistrictChange = (newDistrict: District | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.providerDistrict = newDistrict?.code;
    autoInvoiceRef.current.providerDistrictDescription =
      newDistrict?.description;
    setProviderDistrict(newDistrict);
    if (newDistrict === null) {
      setProviderCity(null);
      autoInvoiceRef.current.providerCity = undefined;
      autoInvoiceRef.current.providerCityDescription = undefined;
    }
  };

  const onProviderCityChange = (newCity: City | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.providerCity = newCity?.code;
    autoInvoiceRef.current.providerCityDescription = newCity?.description;
    setProviderCity(newCity);
  };

  const onTransactionAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.transactionAddress = e.target.value;
  };

  const onTransactionHouseNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (autoInvoiceRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      autoInvoiceRef.current.transactionHouseNumber = parseInt(e.target.value);
    } else {
      autoInvoiceRef.current.transactionHouseNumber = undefined;
    }
  };

  const onTransactionDepartmentChange = (newDepartment: Department | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.transactionDepartment = newDepartment?.code;
    autoInvoiceRef.current.transactionDepartmentDescription =
      newDepartment?.description;
    setTransactionDepartment(newDepartment);
    if (newDepartment === null) {
      setTransactionDistrict(null);
      setTransactionCity(null);
      autoInvoiceRef.current.transactionDistrict = undefined;
      autoInvoiceRef.current.transactionDistrictDescription = undefined;
      autoInvoiceRef.current.transactionCity = undefined;
      autoInvoiceRef.current.transactionCityDescription = undefined;
    }
  };

  const onTransactionDistrictChange = (newDistrict: District | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.transactionDistrict = newDistrict?.code;
    autoInvoiceRef.current.transactionDistrictDescription =
      newDistrict?.description;
    setTransactionDistrict(newDistrict);
    if (newDistrict === null) {
      setTransactionCity(null);
      autoInvoiceRef.current.transactionCity = undefined;
      autoInvoiceRef.current.transactionCityDescription = undefined;
    }
  };

  const onTransactionCityChange = (newCity: City | null) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.transactionCity = newCity?.code;
    autoInvoiceRef.current.transactionCityDescription = newCity?.description;
    setTransactionCity(newCity);
  };

  const onCertificateNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.certificateNumber = e.target.value;
  };

  const onCertificateCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.certificateCode = e.target.value;
  };

  const onTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.term = e.target.value;
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.description = e.target.value;
  };

  const onObsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoInvoiceRef.current === null) return;
    autoInvoiceRef.current.obs = e.target.value;
  };

  const onItemChange = (index: number, item: AutoInvoiceItem) => {
    if (autoInvoiceRef.current === null) return;
    const newItems = [...autoInvoiceRef.current.items];
    newItems[index] = item;
    onItemsChange(newItems);
  };

  const onItemDelete = (index: number) => {
    if (autoInvoiceRef.current === null) return;
    const newItems = [...autoInvoiceRef.current.items];
    newItems.splice(index, 1);
    onItemsChange(newItems);
  };

  const onItemsChange = (newItems: AutoInvoiceItem[]) => {
    if (autoInvoiceRef.current === null) return;

    // Calculate totals
    let totalDiscount = 0;
    let totalAmount = 0;

    newItems.forEach((item) => {
      if (item.discount !== undefined) {
        totalDiscount += item.discount;
      }
      if (item.totalAmount !== undefined) {
        totalAmount += item.totalAmount;
      }
    });

    let amountAfterDiscount = totalAmount - totalDiscount;

    if (applyDiscount && globalDiscount !== undefined) {
      amountAfterDiscount -= globalDiscount;
    }

    autoInvoiceRef.current = {
      ...autoInvoiceRef.current,
      items: newItems,
      amount: totalAmount,
      totalDiscount,
      amountAfterDiscount,
    };

    setItems(newItems);
    setTotalAmount(amountAfterDiscount);
  };

  const onAddItemClick = () => {
    if (autoInvoiceRef.current === null) return;
    const newItems = [...autoInvoiceRef.current.items, newAutoInvoiceItem()];
    onItemsChange(newItems);
  };

  const value: AutoInvoiceFormContextValue = {
    autoInvoiceRef,
    autoInvoiceDate,
    currency,
    pygExchangeRate,
    totalAmount,
    applyDiscount,
    globalDiscount,
    establishment,
    dispatchPoint,
    stamp,
    providerDepartment,
    providerDistrict,
    providerCity,
    transactionDepartment,
    transactionDistrict,
    transactionCity,
    items,
    errors,
    readonly,
    setAutoInvoiceDate,
    setCurrency,
    setPygExchangeRate,
    setApplyDiscount,
    setEstablishment,
    setDispatchPoint,
    setStamp,
    setProviderDepartment,
    setProviderDistrict,
    setProviderCity,
    setTransactionDepartment,
    setTransactionDistrict,
    setTransactionCity,
    setItems,
    setErrors,
    onEstablishmentChange,
    onDispatchPointChange,
    onStampChange,
    onAutoInvoiceDateChange,
    onCurrencyChange,
    onPygExchangeRateChange,
    onApplyDiscountChange,
    onGlobalDiscountChange,
    onProviderTypeChange,
    onProviderDocumentTypeChange,
    onProviderDocumentNumberChange,
    onProviderFullNameChange,
    onProviderAddressChange,
    onProviderHouseNumberChange,
    onProviderDepartmentChange,
    onProviderDistrictChange,
    onProviderCityChange,
    onTransactionAddressChange,
    onTransactionHouseNumberChange,
    onTransactionDepartmentChange,
    onTransactionDistrictChange,
    onTransactionCityChange,
    onCertificateNumberChange,
    onCertificateCodeChange,
    onTermChange,
    onDescriptionChange,
    onObsChange,
    onItemChange,
    onItemDelete,
    onItemsChange,
    onAddItemClick,
  };

  return (
    <AutoInvoiceFormContext.Provider value={value}>
      {children}
    </AutoInvoiceFormContext.Provider>
  );
};
